/**
 * AddMembersModal - Reusable modal to select users (and optional role) and confirm additions.
 *
 * Used by:
 * - ProjectMembers (add members to a project)
 * - TeamDetails (add members to a team)
 */
import { useEffect, useMemo, useState } from 'react';
import { Team } from '@/types';
import { Search, Loader2 } from 'lucide-react';

export type MemberRole = 'admin' | 'member' | 'viewer';

interface AddTeamsModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  availableTeams: Team[];
  loadingTeams?: boolean;

  /** Allow multiple selection (default: false - single selection) */
  allowMultiple?: boolean;

  /** Called when user closes the modal (via X or Cancel). */
  onClose: () => void;

  /**
   * Called with selected team ID(s) when user confirms.
   * If allowMultiple is false, will be called with single team ID string.
   * If allowMultiple is true, will be called with array of team IDs.
   * If it returns a Promise, the modal will show a loading state until resolved.
   */
  onConfirm: (selectedTeamIds: string | string[]) => void | Promise<void>;

  /** Override confirm button label (defaults to "Assign Team" or "Add Teams"). */
  confirmLabel?: string;
}

export const AddTeamsModal = ({
  isOpen,
  title,
  description,
  availableTeams,
  loadingTeams,
  allowMultiple = false,
  onClose,
  onConfirm,
  confirmLabel,
}: AddTeamsModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Reset local state when modal is opened/closed
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIds(new Set());
      setSubmitting(false);
    }
  }, [isOpen]);

  const filteredTeams = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableTeams;
    return availableTeams.filter((team) => {
      return team.name.toLowerCase().includes(q) || team.description?.toLowerCase().includes(q);
    });
  }, [availableTeams, searchQuery]);

  if (!isOpen) return null;

  const handleToggleTeam = (teamId: string, checked: boolean) => {
    if (allowMultiple) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(teamId);
        else next.delete(teamId);
        return next;
      });
    } else {
      // Single selection - replace the set
      setSelectedIds(checked ? new Set([teamId]) : new Set());
    }
  };

  const handleConfirm = async () => {
    if (selectedIds.size === 0 || submitting) return;

    try {
      setSubmitting(true);
      if (allowMultiple) {
        await onConfirm(Array.from(selectedIds));
      } else {
        const teamId = Array.from(selectedIds)[0];
        await onConfirm(teamId);
      }
      // Let parent decide whether to close, but reset local selection
      setSelectedIds(new Set());
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selectedIds.size;
  const defaultConfirmLabel = allowMultiple ? 'Add Teams' : 'Assign Team';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {description && (
              <p className="text-xs text-gray-500 mt-1">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search teams by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Role selection (optional) */}
          {/* {showRoleSelector && (
            <div className="mb-4 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Role:</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRole('member')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    role === 'member'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Member
                </button>
                <button
                  type="button"
                  onClick={() => setRole('admin')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    role === 'admin'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Admin
                </button>
                <button
                  type="button"
                  onClick={() => setRole('viewer')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    role === 'viewer'
                      ? 'bg-gray-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Viewer
                </button>
              </div>
            </div>
          )} */}

          {/* Selected Count */}
          {selectedCount > 0 && (
            <div className="mb-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
              <p className="text-sm text-primary-700">
                <strong>{selectedCount}</strong> team
                {selectedCount !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}

          {/* Teams list */}
          <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
            {loadingTeams ? (
              <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading teams...</span>
              </div>
            ) : filteredTeams.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {searchQuery
                  ? 'No teams found matching your search'
                  : 'No teams available'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredTeams.map((team) => (
                  <label
                    key={team.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type={allowMultiple ? 'checkbox' : 'radio'}
                      name={allowMultiple ? undefined : 'team-selection'}
                      checked={selectedIds.has(team.id)}
                      onChange={(e) => handleToggleTeam(team.id, e.target.checked)}
                      className={allowMultiple 
                        ? 'w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500'
                        : 'w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                      }
                    />
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-medium">
                        {team.name.substring(0, 2).toUpperCase()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {team.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {team.description || 'No description'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedCount === 0 || submitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {allowMultiple ? 'Adding...' : 'Assigning...'}
              </>
            ) : (
              confirmLabel || defaultConfirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


